// src/utils/index.js

export function formatDateTime(date) {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d)) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function validateEmail(email) {
  return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
    String(email).toLowerCase()
  );
}

// Expresses time remaining in words/abbreviations
// Accepts a future date (Date object or string) or a number of ms
// If more than a day: "X days Yh"
// If less than a day: "Xh Ym"
// Returns '' if date is in the past
export function formatTimeRemaining(future, verbose = false) {
  let ms;
  if (typeof future === 'number') {
    ms = future;
  } else if (future instanceof Date || typeof future === 'string') {
    const d = typeof future === 'string' ? new Date(future) : future;
    if (isNaN(d)) return '';
    ms = d.getTime() - Date.now();
  } else {
    return '';
  }
  if (typeof ms !== 'number' || isNaN(ms) || ms < 0) return '';
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / 1440); // 1440 min in a day
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) {
    if (verbose) {
      return `${days} day${days !== 1 ? 's' : ''}${hours > 0 ? ' ' + hours + ' hour' + (hours !== 1 ? 's' : '') : ''}`;
    } else {
      return `${days} day${days !== 1 ? 's' : ''}${hours > 0 ? ' ' + hours + 'h' : ''}`;
    }
  } else if (hours > 0 || minutes > 0) {
    if (verbose) {
      let str = '';
      if (hours > 0) {
        str += `${hours} hour${hours !== 1 ? 's' : ''}`;
      }
      if (minutes > 0) {
        if (str) str += ' ';
        str += `${minutes} minute${minutes !== 1 ? 's' : ''}`;
      }
      return str.trim();
    } else {
      let str = '';
      if (hours > 0) {
        str += `${hours}h`;
      }
      if (minutes > 0) {
        if (str) str += ' ';
        str += `${minutes}m`;
      }
      return str.trim();
    }
  } else {
    // less than 1 minute
    return verbose ? '< 1 minute' : '< 1m';
  }
}

export function getExpirationUrgencyStyle(expiresAt, nowMs = Date.now()) {
  if (!expiresAt) return {};

  const remainingMs = new Date(expiresAt).getTime() - nowMs;
  if (Number.isNaN(remainingMs)) return {};
  const oneHourMs = 60 * 60 * 1000;
  const oneDayMs = 24 * 60 * 60 * 1000;

  if (remainingMs >= oneDayMs) {
    return { color: 'var(--text2)' };
  }

  if (remainingMs <= oneHourMs) {
    return { color: '#ff3b30' };
  }

  const rampRangeMs = oneDayMs - oneHourMs;
  const progress = Math.max(0, Math.min(1, (oneDayMs - remainingMs) / rampRangeMs));
  const startRgb = resolveTextSecondaryRgb();
  const endRgb = [255, 59, 48];
  const r = Math.round(startRgb[0] + (endRgb[0] - startRgb[0]) * progress);
  const g = Math.round(startRgb[1] + (endRgb[1] - startRgb[1]) * progress);
  const b = Math.round(startRgb[2] + (endRgb[2] - startRgb[2]) * progress);

  return {
    color: `rgb(${r}, ${g}, ${b})`,
  };
}

export function scrollToTop(containerSelector) {
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }

  if (typeof document === 'undefined' || !containerSelector) return;

  const container = document.querySelector(containerSelector);
  if (container && 'scrollTo' in container) {
    container.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }
}

import { appStorage } from '../../../../_shared/utils/storage.js';

export function clearClientAuthStorage() {
  appStorage.clear();

  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.clear();
    }
  } catch (error) {
    console.warn('Failed to clear sessionStorage', error);
  }
}

export function shouldShowListControlBar(items, minimumItems = 5) {
  return Array.isArray(items) && items.length >= minimumItems;
}

function resolveTextSecondaryRgb() {
  const fallback = [182, 197, 207];
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return fallback;
  }

  const cssColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--text2')
    .trim();

  return parseCssColorToRgb(cssColor) || fallback;
}

function parseCssColorToRgb(color) {
  if (!color) return null;

  const hex = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const value = hex[1];
    if (value.length === 3) {
      return value.split('').map((ch) => parseInt(ch + ch, 16));
    }
    return [
      parseInt(value.slice(0, 2), 16),
      parseInt(value.slice(2, 4), 16),
      parseInt(value.slice(4, 6), 16),
    ];
  }

  const rgb = color.match(/^rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) {
    return [
      Math.max(0, Math.min(255, Number(rgb[1]))),
      Math.max(0, Math.min(255, Number(rgb[2]))),
      Math.max(0, Math.min(255, Number(rgb[3]))),
    ];
  }

  return null;
}

export function getServiceAbbr(location) {
  const dataShim = {
    host: location.host,
    path: location.pathname,
    url: location.href,
  };

  const serviceList = ['mailbox', 'notes', 'directory', 'study', 'sux'];
  const host = dataShim.host || '';
  const pathSegments = dataShim.path.toLowerCase().split('/');
  var lowerPath;
  for(var i = 0; i <= 5; i++) {
    if (serviceList.includes(pathSegments[i])) {
      lowerPath = pathSegments[i];
    }
  }

  if (host.includes('brownjuice') && !serviceList.includes(lowerPath)) {
    console.log('Failed to get service name. Invalid path')
  }

  switch (true) {
    case (host.includes('formbolt')):
    case (lowerPath === 'mailbox'): return 'mailbox';

    case (host.includes('designexternship')):
    case (lowerPath === 'study'): return 'study';

    // case (host.includes('scheduleux')):
    case (lowerPath === 'sux'): return 'sux';

    // case (host.includes('co-note')):
    case (lowerPath === 'notes'): return 'notes';

    // case (host.includes('agency-book')):
    case (lowerPath === 'contacts'): return 'contacts';

    default: return 'bjco';
  }
}
